import { Link, useParams } from 'react-router-dom';
import { diagnosticsApi, reportContentUrl } from '../api/endpoints';
import { Badge } from '../components/Badge';
import { ConfigurationComparison } from '../components/ConfigurationComparison';
import { Notice } from '../components/Notice';
import { LocalReportNotice } from '../desktop/LocalReportNotice';
import {
  CONFIGURATION_LABELS,
  diagnosticTypeLabel,
  fileFormatLabel,
  isMonospaceFormat,
} from '../domain/diagnostics';
import {
  workstationStateLabel,
  workstationStateTone,
  type WorkstationState,
} from '../domain/workstations';
import { useAsync } from '../hooks/useAsync';
import { formatDateTime, formatSize } from '../lib/format';

function MetadataList({ values }: { values: Record<string, unknown> }) {
  const entries = Object.entries(values).filter(
    ([, value]) => value !== null && value !== undefined && value !== '',
  );
  if (entries.length === 0) return <p className="doux">Aucune valeur.</p>;

  return (
    <dl className="definitions">
      {entries.map(([key, value]) => (
        <div key={key} style={{ display: 'contents' }}>
          <dt>{CONFIGURATION_LABELS[key] ?? key}</dt>
          <dd>{Array.isArray(value) ? value.map(String).join(' · ') : String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function withoutLlmTrace(values: Record<string, unknown>): Record<string, unknown> {
  const { llm: _llm, ...rest } = values;
  return rest;
}

/**
 * L'information utile est l'état qu'avait le poste ce jour-là. Quand il diffère
 * de son état actuel, l'écart est montré : c'est lui qui renseigne.
 */
function StateAtDiagnostic({
  atDiagnostic,
  current,
}: {
  atDiagnostic: WorkstationState | null;
  current: WorkstationState | null;
}) {
  return (
    <div className="ligne" style={{ marginTop: 'var(--espace-3)' }}>
      <span className="faible">État du poste à la date du diagnostic :</span>
      <Badge tone={workstationStateTone(atDiagnostic)}>
        {workstationStateLabel(atDiagnostic)}
      </Badge>
      {atDiagnostic !== current && (
        <>
          <span className="faible">— état actuel du poste :</span>
          <Badge tone={workstationStateTone(current)}>
            {workstationStateLabel(current)}
          </Badge>
        </>
      )}
    </div>
  );
}

export function ReportViewerPage() {
  const { id = '' } = useParams();

  const diagnostic = useAsync((signal) => diagnosticsApi.get(id, signal), [id]);
  const contenuUrl = reportContentUrl(id);

  if (diagnostic.error !== null) {
    return (
      <Notice tone="erreur" title="Diagnostic introuvable">
        {diagnostic.error}
      </Notice>
    );
  }

  const detail = diagnostic.data?.diagnostic ?? null;
  if (detail === null) return <div className="chargement">Chargement du diagnostic…</div>;

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>{diagnosticTypeLabel(detail.type)}</h1>
          <p className="entete-page__sous-titre">
            <Link to={`/postes/${detail.workstationId}`}>{detail.workstationName}</Link> ·{' '}
            {formatDateTime(detail.performedAt)}
          </p>
          <StateAtDiagnostic
            atDiagnostic={detail.stateAtDiagnostic}
            current={detail.workstationCurrentState}
          />
        </div>
        <div className="ligne">
          {detail.cancelled && <Badge tone="danger">Import annulé</Badge>}
          {/*
            Une navigation vers cette adresse n'est pas un chargement en cadre :
            le serveur répond en pièce jointe, la page reste affichée.
          */}
          <a className="bouton" href={contenuUrl} download={detail.originalFilename}>
            Télécharger le fichier original
          </a>
        </div>
      </div>

      {detail.cancelled && (
        <Notice tone="alerte" title="Import annulé">
          Un événement d'annulation vise cet import : ce diagnostic ne compte plus comme
          dernier état connu du poste. Le fichier reste conservé.
        </Notice>
      )}

      <div
        style={{
          display: 'grid',
          gap: 'var(--espace-5)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        }}
      >
        <section className="panneau">
          <div className="panneau__entete">
            <span className="panneau__titre">Fichier</span>
          </div>
          <div className="panneau__corps">
            <dl className="definitions">
              <dt>Nom d'origine</dt>
              <dd>{detail.originalFilename}</dd>
              <dt>Format</dt>
              <dd>{fileFormatLabel(detail.fileFormat)}</dd>
              <dt>Taille</dt>
              <dd>{formatSize(detail.sizeBytes)}</dd>
              <dt>Empreinte SHA-256</dt>
              <dd className="mono">{detail.sha256}</dd>
              <dt>Rapport en ligne</dt>
              <dd>
                {detail.reportUrl === null ? (
                  <span className="faible">Non renseigné</span>
                ) : (
                  <a href={detail.reportUrl} target="_blank" rel="noreferrer noopener">
                    userdiag.com
                  </a>
                )}
              </dd>
              <dt>Importé le</dt>
              <dd>{formatDateTime(detail.createdAt)}</dd>
            </dl>
          </div>
        </section>

        <section className="panneau">
          <div className="panneau__entete">
            <span className="panneau__titre">Valeurs confirmées</span>
          </div>
          <div className="panneau__corps pile pile--serree">
            {detail.extractionStatus !== 'ok' && (
              <Notice tone="alerte" title="Extraction sans résultat">
                {detail.extractionError ??
                  "L'extraction automatique n'a rien trouvé dans ce fichier."}
              </Notice>
            )}
            <MetadataList values={detail.confirmedMetadata} />
            {detail.extractionStatus === 'ok' && (
              <details>
                <summary className="doux">Voir les valeurs extraites brutes</summary>
                <div style={{ marginTop: 'var(--espace-3)' }}>
                  <MetadataList values={withoutLlmTrace(detail.extractedMetadata)} />
                </div>
              </details>
            )}
          </div>
        </section>
      </div>

      <section className="panneau">
        <div className="panneau__entete">
          <span className="panneau__titre">Déclaré et observé</span>
          <span className="faible pousse">
            Inventaire à la date du diagnostic, en regard du rapport
          </span>
        </div>
        <div className="panneau__corps">
          <ConfigurationComparison
            diagnosticId={detail.id}
            workstationId={detail.workstationId}
          />
        </div>
      </section>

      <section className="panneau">
        <div className="panneau__entete">
          <span className="panneau__titre">Rapport</span>
          <span className="faible pousse">
            Affiché dans un cadre en origine opaque
          </span>
        </div>
        <div className="panneau__corps visionneuse">
          {/* sans effet dans le navigateur, où le composant ne rend rien */}
          <LocalReportNotice
            diagnosticId={detail.id}
            workstationId={detail.workstationId}
          />
          {/*
            Le retrait de allow-same-origin est ce qui isole le rapport. Sans
            lui, le navigateur attribue à ce document une origine unique,
            différente de toutes les autres et notamment de celle de cette
            page : les scripts du rapport n'atteignent ni le contenu de la
            page, ni le cookie de session, ni l'API.

            Cet attribut vaut exactement « allow-scripts », et rien de plus.
            Sans allow-popups ni allow-top-navigation, le rapport ne peut ni
            ouvrir un onglet ni remplacer la page qui le contient.

            AUCUN problème d'affichage ne justifie d'ajouter allow-same-origin.
            Si un rapport s'affiche mal, la réponse est ailleurs — dans la
            politique de sécurité de contenu posée par le serveur, jamais ici.
            Le vérificateur checks/iframe-sandbox refuse cet ajout.
          */}
          <iframe
            className={
              isMonospaceFormat(detail.fileFormat)
                ? 'visionneuse__cadre visionneuse__cadre--texte'
                : 'visionneuse__cadre'
            }
            src={contenuUrl}
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            title={`Rapport ${detail.originalFilename}`}
          />
          <p className="faible">
            Le rapport est servi par l'application elle-même, dans un cadre dont
            l'origine est opaque. Ouvrir son adresse directement dans un onglet
            déclenche un téléchargement plutôt qu'un affichage.
          </p>
        </div>
      </section>
    </div>
  );
}
